import React, { useState } from 'react';
import { IconAlertTriangle, IconPhoto, IconCaretRightFilled, IconPlus } from '@tabler/icons-react';
import { AppToolTip } from '../../../components/utils/AppToolTip';
import { TableBody, TableCell, TableContainer, TableHead, TableRow, Table, useMediaQuery, TablePagination, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import EditInventoryLinkModal from './modals/EditInventoryLinkModal';
import AddSupplierModal from '../Suppliers/modals/AddSupplierModal';
import AddLocationModal from '../Locations/modals/AddLocationModal';
import AddUOMModal from '../UnitOfMeasure/modals/AddUOMModal';
import CustomPagination from '../../../components/pagination/CustomPagination';
import { useInventoryContext } from '../../../context/inventory_context/InventoryContext';

const InventoryTable = ({ inventory, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, onSelectionChange, isSelectionMode }) => {
  const [showEditLinkModal, setShowEditLinkModal] = useState(false);
  const [itemToEdit, setItemToEdit] = useState();
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [showAddUOMModal, setShowAddUOMModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const { getSupplierByID, getLocationByID } = useInventoryContext();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const { t } = useTranslation();
  
  const handleRowClick = (itemId) => {
    if (!isSelectionMode) {
      navigate(`/inventory/${itemId}`);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = inventory.map((item) => item.id);
      setSelected(newSelected);
      onSelectionChange(newSelected);
    } else {
      setSelected([]);
      onSelectionChange([]);
    }
  };

  const handleCheckboxClick = (event, id) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
    onSelectionChange(newSelected);
  };

  // Reset selection when exiting selection mode
  React.useEffect(() => {
    if (!isSelectionMode) {
      setSelected([]);
      onSelectionChange([]);
    }
  }, [isSelectionMode, onSelectionChange]);

  const isSelected = (id) => selected.indexOf(id) !== -1;

  return (
    <>
      <TableContainer style={{ overflowX: 'auto' }}>
        <Table size={'medium'} aria-label="inventory-table">
          <TableHead className="users-table-header">
            <TableRow>
              {isSelectionMode && !isMobile && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < inventory.length}
                    checked={inventory.length > 0 && selected.length === inventory.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
              )}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}></TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.IMAGE)}</TableCell>}
              <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.NAME)}</TableCell>
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.PRODUCT_SKU)}</TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.ON_HAND)}</TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.MINIMUM_QUANTITY)}</TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.REORDER_QUANTITY)}</TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.UNIT_OF_MEASURE)}</TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.SUPPLIER)}</TableCell>}
              {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.LOCATION)}</TableCell>}
              {isMobile && <TableCell></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {inventory.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => {
              const isItemSelected = isSelected(item.id);
              return (
                <TableRow 
                  key={index} 
                  onClick={() => handleRowClick(item.id)} 
                  style={{ cursor: !isMobile && !isSelectionMode ? 'pointer' : 'default' }}
                  selected={isItemSelected}
                >
                  {isSelectionMode && !isMobile && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isItemSelected}
                        onChange={(event) => handleCheckboxClick(event, item.id)}
                      />
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      {parseInt(item.quantity) <= parseInt(item.reorder_quantity) && (
                        <AppToolTip
                          icon={
                            <IconAlertTriangle
                              style={{
                                color: "red",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer"
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(item.link, '_blank');
                              }}
                            />
                          }
                          content={t(StringKeys.QUANTITY_ON_HAND_HAS_REACHED_OR_SURPASSED_YOUR_REORDER_QUANTITY)}
                        />
                      )}
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={"Item Image"}
                          style={{ width: '40px', height: '40px', marginRight: '10px' }}
                        />
                      ) : (
                        <IconPhoto size={40} />
                      )}
                    </TableCell>
                  )}
                  <TableCell>{item.name}</TableCell>
                  {!isMobile && <TableCell>{item.sku}</TableCell>}
                  {!isMobile && <TableCell>{item.quantity}</TableCell>}
                  {!isMobile && <TableCell>{item.minimum_quantity}</TableCell>}
                  {!isMobile && <TableCell>{item.reorder_quantity}</TableCell>}
                  {!isMobile && <TableCell>{item.unit_of_measure || t(StringKeys.UNASSIGNED)}</TableCell>}
                  {!isMobile && <TableCell>{getSupplierByID(item.supplier)?.name || t(StringKeys.UNASSIGNED)}</TableCell>}
                  {!isMobile && <TableCell>{getLocationByID(item.location)?.name || t(StringKeys.UNASSIGNED)}</TableCell>}
                  {isMobile && (
                    <TableCell style={{ textAlign: "end" }}>
                      <IconCaretRightFilled
                        onClick={(e) => {
                          e.stopPropagation(); 
                          handleRowClick(item.id);
                        }}
                        style={{ cursor: "pointer" }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomPagination
        rowsPerPageOptions={[10, 25, 100, { label: 'All', value: -1 }]}
        component="div"
        count={inventory.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <EditInventoryLinkModal
        show={showEditLinkModal}
        onHide={() => {
          setItemToEdit(null);
          setShowEditLinkModal(false);
        }}
        item={itemToEdit}
      />
      <AddSupplierModal
        show={showAddSupplierModal}
        onHide={() => setShowAddSupplierModal(false)}
      />
      <AddLocationModal
        show={showAddLocationModal}
        onHide={() => setShowAddLocationModal(false)}
      />
      <AddUOMModal
        show={showAddUOMModal}
        onHide={() => setShowAddUOMModal(false)}
      />
    </>
  );
};

export default InventoryTable;
